/**
 * Test/Live mode selection for the Client Dashboard (docs/WALKTHROUGH.md's known-gaps section:
 * "the Dashboard never sends a mode param, so it can only ever see live-mode data"). The server
 * (apps/api/src/middleware/tenantResolution.ts) resolves mode per-request as
 * `X-Loctopus-Mode header ?? mode query param ?? 'live'` and keeps enforcing mode isolation and
 * every permission exactly as before - this module only decides what the CLIENT asks for. It
 * never weakens anything server-side.
 *
 * State lives in a module-level variable (read once, synchronously, by `lib/api.ts`'s `request()`
 * on every call) and is mirrored into `sessionStorage` so a page reload mid-session keeps the same
 * choice - explicitly sessionStorage, not localStorage, per "remember the choice per session", and
 * wrapped in try/catch since storage can throw or be unavailable (private browsing, disabled
 * cookies/storage, etc.) - the module must still work with in-memory state only if so.
 */

const STORAGE_KEY = 'loctopus_dashboard_mode';

export type DashboardMode = 'test' | 'live';

function isDashboardMode(value: unknown): value is DashboardMode {
  return value === 'test' || value === 'live';
}

/** Development builds default to Test (safe - nobody accidentally stares at real orders while
 * poking around locally); a real deployed build defaults to Live (what a tenant actually expects
 * to see first). Either way it's just the starting point - the switch always overrides it. */
const DEFAULT_MODE: DashboardMode = import.meta.env.DEV ? 'test' : 'live';

function readStoredMode(): DashboardMode {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (isDashboardMode(stored)) return stored;
  } catch {
    // sessionStorage unavailable - fall through to the default, in-memory only.
  }
  return DEFAULT_MODE;
}

let currentMode: DashboardMode = readStoredMode();
const listeners = new Set<(mode: DashboardMode) => void>();

export function getMode(): DashboardMode {
  return currentMode;
}

export function setMode(mode: DashboardMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  try {
    sessionStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // best-effort persistence only - the in-memory value above is still correct for this tab.
  }
  for (const listener of listeners) listener(mode);
}

/** Subscribes to mode changes; returns an unsubscribe function. Used by `useMode()` below so every
 * component reading the mode re-renders the instant the switch flips, without prop-drilling. */
export function subscribeMode(listener: (mode: DashboardMode) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
